import { Segment, TimeRange, Timestamp } from '@mi-front/core';
import { ClickableGraphArea, SegmentsStream } from '@mi-front/ui';
import { Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { toTimestampRange } from '@app/helpers/range';
import {
  STREAM_HEIGHT,
  STREAM_HEIGHT_COMPACT,
  THUMB_PREVIEW_WIDTH,
  THUMB_PREVIEW_WIDTH_COMPACT,
} from '@app/modules/logger/screens/status/constants';
import { computeGapsBetweenSegments } from '../../../../stream/helpers/compute-gaps-between-segments';
import { useStatusViewContext } from '../../../context/StatusViewContext';
import { useEffectiveItemWidth } from '../../../hooks/useEffectiveItemWidth';
import { StatusThumbnail } from './thumb/StatusThumbnail';
import { Station } from '@app/core/types/stations';

interface Props {
  readonly station: Station;
  readonly segments: readonly Segment[];
  readonly range: TimeRange;
}

/**
 * Video status preview component that displays segments data in a visual stream format.
 * For TV stations, reduces waveform width by 30% and displays thumbnail on the left.
 * Supports hover functionality to show thumbnail at hovered timestamp.
 */
export function VideoStatusPreview(p: Props) {
  const { station, segments, range } = p;
  const { compact } = useStatusViewContext();
  const effectiveWidth = useEffectiveItemWidth();
  const history = useHistory();

  const [hoveredTs, setHoveredTs] = useState<Timestamp | null>(null);

  const thumbContainerSize = compact ? THUMB_PREVIEW_WIDTH_COMPACT : THUMB_PREVIEW_WIDTH;
  const waveformWidth = effectiveWidth - thumbContainerSize;

  const timestampRange = toTimestampRange(range);

  // Compute gaps between segments to make them un-clickable
  const unclickableRanges = useMemo(
    () => computeGapsBetweenSegments(segments, timestampRange),
    [segments, range.from, range.to],
  );

  const onBlurHandler = useCallback(() => {
    setHoveredTs(null);
  }, []);

  // Waveform captures pointer events (for hover preview), so navigate explicitly —
  // the StatusCard stretch link underneath no longer receives those clicks.
  const onTsClickHandler = useCallback(() => {
    history.push(`/logger/s/${station.slug}`);
  }, [history, station.slug]);

  if (effectiveWidth <= 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <Box
        data-testid="station-thumbnail"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <StatusThumbnail station={station} segments={segments} hoveredTs={hoveredTs} />
      </Box>
      <Box
        data-testid="station-waveform"
        className="status-card__waveform"
        sx={{
          width: waveformWidth,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SegmentsStream
          tz={station.timezone}
          height={compact ? STREAM_HEIGHT_COMPACT : STREAM_HEIGHT}
          width={waveformWidth}
          range={timestampRange}
          segments={segments}
          fadeSides="left"
          hideSeconds={compact}
          renderHtml={() => (
            <ClickableGraphArea
              tz={station.timezone}
              onHover={setHoveredTs}
              onBlur={onBlurHandler}
              showHoverCursor
              cursorType="dot"
              unclickableRanges={unclickableRanges}
              showHoverTimeLabel={false}
              onTsClick={onTsClickHandler}
            />
          )}
        />
      </Box>
    </Box>
  );
}
